import type { ReleaseClient } from '../api/client';
import type { ReleaseRequest, ReleaseData } from '../types/release';
import type { ValidationData, ValidationResponse } from '../types/api';

export interface WorkflowCallbacks {
	onGenerationStart?: () => void;
	onGenerationComplete?: (data: ReleaseData) => void;
	onValidationComplete?: (data: ValidationData) => void;
	onDeliveryStart?: () => void;
	onDeliveryComplete?: (filePath: string) => void;
	onError?: (error: Error) => void;
}

export interface WorkflowState {
	isGenerating: boolean;
	isValidating: boolean;
	isDelivering: boolean;
	generatedData: ReleaseData | null;
	validationData: ValidationData | null;
	deliveryPath: string | null;
	error: Error | null;
}

export interface UseReleaseWorkflowOptions {
	client: ReleaseClient;
	callbacks?: WorkflowCallbacks;
}

/**
 * Create a workflow controller instance.
 *
 * Every step receives the original request for format-awareness.
 * The request's outputFormat drives routing decisions throughout.
 */
export function useReleaseWorkflow(options: UseReleaseWorkflowOptions) {
	const { client, callbacks = {} } = options;

	async function generateRelease(request: ReleaseRequest): Promise<ReleaseData> {
		try {
			callbacks.onGenerationStart?.();

			const response = await client.post<{ data?: ReleaseData; error?: { message: string } }>(
				'/api/releases/generate',
				request
			);

			if (!response || (response as any).error) {
				throw new Error((response as any)?.error?.message ?? 'Failed to generate release');
			}

			const data = (response as any).data as ReleaseData | undefined;
			if (!data?.xml) {
				throw new Error('No XML received from server');
			}

			callbacks.onGenerationComplete?.(data);
			return data;
		} catch (error) {
			const err = error instanceof Error ? error : new Error('Unknown generation error');
			callbacks.onError?.(err);
			throw err;
		}
	}

	async function validateXml(data: ReleaseData, request: ReleaseRequest): Promise<ValidationData> {
		try {
			const validationResponse = await client.post<ValidationResponse>('/api/releases/validate', {
				xml: data.xml,
				ernVersion: request.ernVersion,
				avsVersion: request.avsVersion,
				outputFormat: request.outputFormat
			});

			if (!validationResponse.data?.valid) {
				throw new Error(validationResponse.data?.message ?? 'Validation failed');
			}

			callbacks.onValidationComplete?.(validationResponse.data);
			return validationResponse.data;
		} catch (error) {
			const err = error instanceof Error ? error : new Error('Unknown validation error');
			callbacks.onError?.(err);
			throw err;
		}
	}

	/**
	 * Deliver the release package.
	 *
	 * Uses request.outputFormat to route to the correct backend.
	 * Release ID is derived from the format:
	 *   - DDEX: ICPN or ISRC (from releaseId object)
	 *   - Apple: deliveryId (vendor ID from generate response)
	 */
	async function deliverRelease(data: ReleaseData, request: ReleaseRequest): Promise<string> {
		try {
			callbacks.onDeliveryStart?.();

			const isApple = request.outputFormat === 'apple-itunes';

			// Derive release ID based on format
			let releaseId: string | undefined;
			if (isApple) {
				if (!data.deliveryId) {
					throw new Error('No delivery ID received from generation');
				}
				releaseId = data.deliveryId;
			} else {
				releaseId = request.releaseType === 'SingleResourceRelease'
					? request.releaseId?.proprietaryId?.value
					: request.releaseId?.ICPN;
			}

			const body: Record<string, unknown> = {
				releaseId,
				xml: data.xml,
				outputFormat: request.outputFormat,
			};
			if (!isApple) {
				body.priority = 'N';
				body.messageHeader = data.messageHeader;
				body.releaseProfile = data.releaseProfile;
				body.releaseType = request.releaseType;
			}

			const response = await client.post<{ data?: { filePath: string }; error?: { message: string } }>(
				'/api/releases/deliver',
				body
			);

			if (!response || (response as any).error) {
				throw new Error((response as any).error?.message ?? 'Delivery failed');
			}

			const filePath = (response as any).data?.filePath;
			if (!filePath) {
				throw new Error('No file path received');
			}

			callbacks.onDeliveryComplete?.(filePath);
			return filePath;
		} catch (error) {
			const err = error instanceof Error ? error : new Error('Unknown delivery error');
			callbacks.onError?.(err);
			throw err;
		}
	}

	async function runFullWorkflow(request: ReleaseRequest): Promise<ReleaseData> {
		try {
			const data = await generateRelease(request);
			await validateXml(data, request);
			return data;
		} catch (error) {
			throw error;
		}
	}

	return {
		generateRelease,
		validateXml,
		deliverRelease,
		runFullWorkflow
	};
}

export function createStatefulWorkflow(client: ReleaseClient) {
	const state: WorkflowState = {
		isGenerating: false,
		isValidating: false,
		isDelivering: false,
		generatedData: null,
		validationData: null,
		deliveryPath: null,
		error: null
	};

	const callbacks: WorkflowCallbacks = {
		onGenerationStart: () => {
			state.isGenerating = true;
			state.error = null;
		},
		onGenerationComplete: (data) => {
			state.isGenerating = false;
			state.generatedData = data;
		},
		onValidationComplete: (data) => {
			state.isValidating = false;
			state.validationData = data;
		},
		onDeliveryStart: () => {
			state.isDelivering = true;
		},
		onDeliveryComplete: (filePath) => {
			state.isDelivering = false;
			state.deliveryPath = filePath;
		},
		onError: (error) => {
			state.isGenerating = false;
			state.isValidating = false;
			state.isDelivering = false;
			state.error = error;
		}
	};

	const workflow = useReleaseWorkflow({ client, callbacks });

	return {
		...workflow,
		getState: () => ({ ...state }),
		reset: () => {
			state.isGenerating = false;
			state.isValidating = false;
			state.isDelivering = false;
			state.generatedData = null;
			state.validationData = null;
			state.deliveryPath = null;
			state.error = null;
		}
	};
}
